/**
 * The UI-local intents (P2 design §4.6): one call per user action, from the handler that
 * performs it. Each opens and ends its intent at once (`withIntent`), or, for the few with
 * server work, returns the handle so the caller ends it with the request's outcome. Params are
 * ids, enums and numbers only — never text (a reference's domain, never its URL; a copy's
 * length, never its content).
 */
import { beginIntent, withIntent } from "./instrument";

/** An id-shaped key, as the capture accepts them (`[A-Za-z0-9._:-]{1,64}`), or null. */
const ID_SHAPE = /^[A-Za-z0-9._:-]{1,64}$/;
export const idShaped = (v) =>
  (typeof v === "string" || typeof v === "number") && ID_SHAPE.test(String(v)) ? String(v) : null;

/**
 * An evidence card's telemetry key: its `card_id`, else its report label (`E3`) when that is
 * id-shaped. The same rule keys its `evidence.card` surface and its `data-ui` controls, so a
 * card without a `card_id` still joins its span to its expand / collapse intents.
 */
export function evidenceCardKey(card) {
  return idShaped(card?.cardId) ?? idShaped(card?.label);
}

/** `evidence.card_expand` / `evidence.card_collapse` for `card`. */
export function emitCardToggle(open, card, { forecastId = null } = {}) {
  const params = { card_id: evidenceCardKey(card), forecast_id: forecastId };
  if (open) {
    withIntent("evidence.card_expand", () => undefined, params, {
      origin: "evidence.card-expand",
      surface: "evidence.card",
    });
  } else {
    withIntent("evidence.card_collapse", () => undefined, params, {
      origin: "evidence.card-collapse",
      surface: "evidence.card",
    });
  }
}

/** `www.example.com/…` → `example.com`; null for anything that is not an http(s) URL. */
export function referenceDomain(url) {
  try {
    const u = new URL(String(url));
    if (u.protocol !== "http:" && u.protocol !== "https:") return null;
    return u.hostname.replace(/^www\./, "").slice(0, 128) || null;
  } catch {
    return null;
  }
}

/**
 * `reference.follow`: a reference followed out of a report — its index in the report
 * (`refId`), its domain, the section and its position there; never the URL.
 */
export function emitReferenceFollow({
  url,
  refId = null,
  section = null,
  position = null,
  forecastId = null,
  origin = "report.reference",
  surface = "report",
} = {}) {
  withIntent(
    "reference.follow",
    () => undefined,
    {
      forecast_id: forecastId,
      ref_id: refId == null ? null : String(refId),
      domain: referenceDomain(url),
      section: idShaped(section),
      position: Number.isInteger(position) ? position : null,
    },
    { origin, surface },
  );
}

/** `report.copy`: text copied out of a report — its section and length, never the text. */
export function emitReportCopy({ section = null, chars = 0, forecastId = null } = {}) {
  withIntent(
    "report.copy",
    () => undefined,
    { forecast_id: forecastId, section: idShaped(section), chars },
    { surface: "report" },
  );
}

/**
 * `export.pdf` / `export.docx`: returns the handle; end it with the export's outcome. `view`
 * "dynamic" names the Dynamic view's controls as the origin.
 */
export function beginExport(kind, { forecastId = null, view = "standard" } = {}) {
  const dynamic = view === "dynamic";
  return kind === "docx"
    ? beginIntent(
        "export.docx",
        { forecast_id: forecastId },
        {
          origin: dynamic ? "report.dynamic-export-docx" : "report.export-docx",
          surface: "report",
        },
      )
    : beginIntent(
        "export.pdf",
        { forecast_id: forecastId },
        {
          origin: dynamic ? "report.dynamic-export-pdf" : "report.export-pdf",
          surface: "report",
        },
      );
}

/** `panel.toggle` for a panel the user opened or closed. */
export function emitPanelToggle(panel, open, { origin = null } = {}) {
  withIntent("panel.toggle", () => undefined, { panel, open: open === true }, { origin });
}

/** `settings.change`: the setting's name only, never its value when that is free text. */
export function emitSettingChange(setting, { origin = null } = {}) {
  withIntent(
    "settings.change",
    () => undefined,
    { setting: idShaped(setting) },
    {
      origin,
      surface: "settings",
    },
  );
}

/** `account.open`. */
export function emitAccountOpen({ origin = null } = {}) {
  withIntent("account.open", () => undefined, {}, { origin });
}

/**
 * `forecast.pin`: returns the handle; end it with the pin request's outcome. A bulk add /
 * remove passes its control as `origin` and how many forecasts it changes as `batch`.
 */
export function beginPin(
  forecastId,
  pinned,
  { origin = "forecast-manager.watch-toggle", batch = 1 } = {},
) {
  return beginIntent(
    "forecast.pin",
    { forecast_id: forecastId, pinned: pinned === true, batch },
    { origin, surface: "forecast-manager" },
  );
}

/** `forecast.reorder`: one forecast moved by hand in the forecast manager. */
export function emitForecastReorder(forecastId) {
  withIntent(
    "forecast.reorder",
    () => undefined,
    { forecast_id: idShaped(forecastId) },
    { surface: "forecast-manager" },
  );
}

/**
 * `stack.arrange`: the user put a card into, out of or within a stack (`join`, `leave`,
 * `move`, `reorder`), made one (`create`), or moved a whole stack in the forecast manager
 * (`place`).
 */
export function emitStackArrange(stackId, action) {
  withIntent(
    "stack.arrange",
    () => undefined,
    { stack_id: idShaped(stackId), action },
    { surface: "forecast-manager" },
  );
}

/**
 * `chat.tier_change`: the forecast depth picked for the next forecast — in the composer
 * (`chat.tier-option`, surface `chat`) unless the picker says where (the ask box's).
 */
export function emitTierChange(tier, { origin = "chat.tier-option", surface = "chat" } = {}) {
  withIntent("chat.tier_change", () => undefined, { tier: idShaped(tier) }, { origin, surface });
}

/**
 * `forecast_settings.change`: which of a forecast's settings changed (cadence, engine,
 * threshold, leader_alert, email), never its value. `origin` is the control, when it has one.
 * Returns the open handle: the write it asks for sends its id as `x-intent-id` and ends it
 * with that write's outcome (useTrackingPrefs: applySchedule, patchTrackingPrefs).
 */
export function beginForecastSettingChange(setting, forecastId, { origin = null } = {}) {
  return beginIntent(
    "forecast_settings.change",
    { setting, forecast_id: idShaped(forecastId) },
    { origin, surface: "forecast-settings" },
  );
}

/** `tour.end`: the tour ended by a skip or by its End Tour button. */
export function emitTourEnd(how) {
  withIntent(
    "tour.end",
    () => undefined,
    { how },
    { origin: how === "completed" ? "tour.end" : "tour.skip", surface: "chat" },
  );
}

/**
 * `listen.play` for a play or replay: returns the open handle, ended with whether the audio
 * actually started (a refused `play()` is an `error`).
 */
export function beginListenPlay({ origin = "listen.play-pause" } = {}) {
  return beginIntent("listen.play", { playing: true }, { origin, surface: "listen" });
}

/** `listen.play`: the Listen briefing played or paused, from a control or a key (`shortcut`). */
export function emitListenPlay(playing, { origin = "listen.play-pause" } = {}) {
  withIntent(
    "listen.play",
    () => undefined,
    { playing: playing === true },
    {
      origin,
      surface: "listen",
    },
  );
}

/** `signal.add`: signals saved to the library, by how they were found and how many. */
export function emitSignalAdd(source, count, { origin = null } = {}) {
  withIntent(
    "signal.add",
    () => undefined,
    { source, count },
    { origin, surface: "signal-library" },
  );
}

/** Whether a settled `apiClient` result (`{ok}`) or a thrown error means success. */
export function outcomeOf(result) {
  return result && result.ok === true ? "ok" : "error";
}

/** The `data-ui` ids of a reference followed out of a report. */
const REFERENCE_UI_IDS = ["report.reference", "evidence.source-link", "report.resolution-source"];
const REFERENCE_SELECTOR = REFERENCE_UI_IDS.map((id) => `a[data-ui="${id}"]`).join(",");

/** The report section (its slug) around `el`, or null. */
function sectionOf(el) {
  return el?.closest?.('[data-surface="report.section"]')?.getAttribute("data-surface-key") ?? null;
}

/**
 * A click inside a report (delegated from its root, ForecastReportModal): when it follows a
 * reference link, `reference.follow` with the link's report index, domain, section and its
 * position among the section's references.
 */
export function onReportClick(event, { forecastId = null } = {}) {
  try {
    const a = event?.target?.closest?.(REFERENCE_SELECTOR);
    if (!a) return;
    const section = sectionOf(a);
    const scope =
      a.closest('[data-surface="report.section"]') ?? a.closest('[data-surface="report"]');
    const links = scope ? Array.from(scope.querySelectorAll(REFERENCE_SELECTOR)) : [];
    const position = links.indexOf(a);
    emitReferenceFollow({
      url: a.getAttribute("href"),
      refId: a.getAttribute("data-ui-key"),
      section,
      position: position >= 0 ? position : null,
      forecastId,
      origin: a.getAttribute("data-ui"),
    });
  } catch {
    /* telemetry never breaks the report */
  }
}

/** Text copied inside a report (delegated from its root): `report.copy` with section and length. */
export function onReportCopy(_event, { forecastId = null, win = globalThis.window } = {}) {
  try {
    const sel = win?.getSelection?.();
    const text = sel ? sel.toString() : "";
    if (!text) return;
    const node = sel.anchorNode;
    const el = node && node.nodeType === 1 ? node : node?.parentElement;
    emitReportCopy({ section: sectionOf(el), chars: text.length, forecastId });
  } catch {
    /* telemetry never breaks the copy */
  }
}

/**
 * The trailing options argument that sends a request under `intent` (`x-intent-id`,
 * lib/apiClient.js), as an argument list to spread: empty while the intent group is off, so
 * calls stay exactly as they were.
 */
export function intentArgs(intent, options = {}) {
  return intent?.id ? [{ ...options, intentId: intent.id }] : [];
}
